import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ErrorBoundary } from './components/error/ErrorBoundary';
import { createLazyRoute } from './utils/routeUtils';
import StandaloneProtectedRoute from './components/auth/StandaloneProtectedRoute';
import { AuthProvider } from './components/auth/AuthProvider';
import { ToastProvider } from './components/layout/ToastProvider';
import { createAppQueryClient } from '@/lib/react-query-client';
import { renderProtectedRoutes } from '@/routes/renderProtectedRoutes';

// Pages outside the admin shell. Admin pages are lazy-loaded from the route
// manifest (src/routes/manifest.ts).
const LoginPage = createLazyRoute(() => import('./pages/auth/LoginPage'));
const PinSwitchScreen = createLazyRoute(() => import('./pages/auth/PinSwitchScreen'));
const VerifySuccessPage = createLazyRoute(() => import('./pages/auth/VerifySuccessPage'));
const SignWaiverPage = createLazyRoute(() => import('./pages/members/SignWaiverPage'));
const WaiverThankYouPage = createLazyRoute(() => import('./pages/members/WaiverThankYouPage'));
const PublicSchedulePage = createLazyRoute(() => import('./pages/public/PublicSchedulePage'));
const PublicClassProfilePage = createLazyRoute(() => import('./pages/public/PublicClassProfilePage'));
const PublicLeaderboardPage = createLazyRoute(() => import('./pages/public/PublicLeaderboardPage'));
const BoothDisplayPage = createLazyRoute(() => import('./pages/public/BoothDisplayPage'));
// Member password reset — the web fallback of the universal link in the reset
// email (the Kairos app claims the same URL on phones). Public, no admin session.
const ResetPasswordPage = createLazyRoute(() => import('./pages/public/ResetPasswordPage'));

// Create QueryClient instance using centralized configuration
const queryClient = createAppQueryClient();

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <BrowserRouter
              future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
              }}
            >
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/verify-success" element={<VerifySuccessPage />} />
                <Route path="/schedule" element={<PublicSchedulePage />} />
                <Route path="/schedule/:id" element={<PublicClassProfilePage />} />
                <Route path="/leaderboard" element={<PublicLeaderboardPage />} />
                <Route path="/booth-display" element={<BoothDisplayPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Standalone pages without sidebar */}
                <Route element={<StandaloneProtectedRoute />}>
                  <Route path="/switch" element={<PinSwitchScreen />} />
                  <Route path="/members/:memberId/sign-waiver" element={<SignWaiverPage />} />
                  <Route path="/members/:memberId/waiver-thank-you" element={<WaiverThankYouPage />} />
                </Route>

                {/* The admin shell. Sections, their URLs and their sidebar links
                    are one list: src/routes/manifest.ts. Add a page there, not
                    here — every route it yields sits behind RequireSection. */}
                {renderProtectedRoutes()}

                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </BrowserRouter>
          </ToastProvider>
        </AuthProvider>
        {/* React Query DevTools - Available in all environments for debugging */}
        <ReactQueryDevtools
          initialIsOpen={false}
          position="bottom"
        />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
